import type { IDonationFrequencySettings, IDonationOptions } from './Api';

/**
 * Calculates smart default amounts for monthly donations based on one-time amounts.
 * Formula: Monthly amount = One-time amount ÷ 2 (rounded down to whole number)
 *
 * Business Rationale: Monthly donors typically give smaller amounts per transaction
 * but contribute more annually. The 2x multiplier balances donor comfort with
 * total annual impact (monthly × 12 = 6× one-time), making recurring donations
 * attractive while remaining affordable for sustained giving.
 *
 * Example: One-time: [25, 50, 100] → Monthly: [12, 25, 50]
 *
 * Note: We use Math.floor instead of Math.round to ensure donation amounts are always
 * whole dollar values, which simplifies payment processing and provides clearer
 * suggested amounts to donors.
 *
 * @param oneTimeAmounts - Array of one-time donation amounts in dollars
 * @returns Array of monthly donation amounts (half of one-time amounts, rounded down)
 */
export const calculateMonthlyDefaults = (oneTimeAmounts: number[]): number[] => {
	return oneTimeAmounts.map(amount => Math.floor(amount / 2));
};

/**
 * Calculates smart default amounts for annual donations based on one-time amounts.
 * Formula: Annual amount = One-time amount × 12
 *
 * Note: We use Math.floor to ensure donation amounts are always whole dollar values,
 * which simplifies payment processing and provides clearer suggested amounts to donors.
 */
export const calculateAnnualDefaults = (oneTimeAmounts: number[]): number[] => {
	return oneTimeAmounts.map(amount => Math.floor(amount * 12));
};

/**
 * Migrates legacy flat donation options structure to the new frequency-specific structure.
 * If the new structure already exists, returns it as-is for backward compatibility.
 * Otherwise, creates oneTimeDonationSettings from legacy fields and generates smart defaults
 * for monthly and annual donations.
 *
 * Edge Cases Handled:
 * - If oneTimeDonationSettings already exists, returns as-is (idempotent)
 * - If suggestedAmounts is undefined/null, defaults to empty array
 * - If minimumAmount is undefined, defaults to 0
 * - Recurring settings only included if allowRecurringDonations is true
 * - Missing legacy fields use safe defaults (false/0/empty array)
 *
 * Data Integrity:
 * - One-time donations are ALWAYS enabled (enabled: true)
 * - Monthly/annual are disabled by default (enabled: false)
 * - Recurring settings inherit most config from legacy one-time settings
 *
 * @param options - The donation options object (may be legacy or new structure)
 * @param allowRecurringDonations - Whether recurring donations are enabled for this survey
 * @returns The donation options with the new frequency-specific structure (safe to use)
 */
export const migrateLegacyDonationOptions = (
	options: IDonationOptions,
	allowRecurringDonations = false
): IDonationOptions => {
	// If new structure already exists, return as-is
	if (options.oneTimeDonationSettings) {
		return options;
	}

	// Create oneTimeDonationSettings from legacy fields
	const oneTimeDonationSettings: IDonationFrequencySettings = {
		enabled: true, // One-time donations are always enabled
		enableSuggestedAmounts: options.enableSuggestedAmounts,
		suggestedAmounts: options.suggestedAmounts || [],
		allowCustomAmount: options.allowCustomAmount,
		requireMinimumAmount: options.requireMinimumAmount,
		minimumAmount: options.minimumAmount || 0,
	};

	// Calculate smart defaults for recurring donations if enabled
	const suggestedAmounts = options.suggestedAmounts || [];
	const monthlyDefaults = calculateMonthlyDefaults(suggestedAmounts);
	const annualDefaults = calculateAnnualDefaults(suggestedAmounts);

	const monthlyDonationSettings: IDonationFrequencySettings = {
		enabled: false, // Disabled by default, user must explicitly enable
		enableSuggestedAmounts: options.enableSuggestedAmounts,
		suggestedAmounts: monthlyDefaults,
		allowCustomAmount: options.allowCustomAmount,
		requireMinimumAmount: options.requireMinimumAmount,
		minimumAmount: options.minimumAmount || 0,
	};

	const annualDonationSettings: IDonationFrequencySettings = {
		enabled: false, // Disabled by default, user must explicitly enable
		enableSuggestedAmounts: options.enableSuggestedAmounts,
		suggestedAmounts: annualDefaults,
		allowCustomAmount: options.allowCustomAmount,
		requireMinimumAmount: options.requireMinimumAmount,
		minimumAmount: options.minimumAmount || 0,
	};

	return {
		...options,
		oneTimeDonationSettings,
		// Only include recurring settings if recurring donations are allowed
		...(allowRecurringDonations
			? {
					monthlyDonationSettings,
					annualDonationSettings,
				}
			: {}),
	};
};

/**
 * Creates a save payload that only includes enabled frequency settings.
 * This prevents sending disabled frequency configurations to the backend.
 *
 * @param options - The donation options with frequency-specific settings
 * @returns A clean donation options object with only enabled frequency settings
 */
export const createDonationOptionsSavePayload = (options: IDonationOptions): IDonationOptions => {
	const payload: IDonationOptions = {
		collectDonorName: options.collectDonorName,
		collectDonorEmail: options.collectDonorEmail,
		collectDonorPhone: options.collectDonorPhone,
		collectDonorAddress: options.collectDonorAddress,
		sendConfirmationEmail: options.sendConfirmationEmail,
		confirmationMessage: options.confirmationMessage,
		donationDescription: options.donationDescription,
		title: options.title,
		image: options.image,

		// Keep legacy fields for backward compatibility with old backend versions
		enableSuggestedAmounts: options.enableSuggestedAmounts,
		suggestedAmounts: options.suggestedAmounts,
		allowCustomAmount: options.allowCustomAmount,
		requireMinimumAmount: options.requireMinimumAmount,
		minimumAmount: options.minimumAmount,
	};

	// Always include oneTimeDonationSettings if present (one-time is always enabled)
	if (options.oneTimeDonationSettings) {
		payload.oneTimeDonationSettings = options.oneTimeDonationSettings;
	}

	// Only include monthly settings if enabled
	if (options.monthlyDonationSettings?.enabled) {
		payload.monthlyDonationSettings = options.monthlyDonationSettings;
	}

	// Only include annual settings if enabled
	if (options.annualDonationSettings?.enabled) {
		payload.annualDonationSettings = options.annualDonationSettings;
	}

	return payload;
};

/**
 * Gets the appropriate frequency settings to use for a given donation frequency.
 * Falls back to one-time settings if the requested frequency is not available.
 *
 * @param options - The donation options object
 * @param frequency - The donation frequency ('OneTime', 'Monthly', or 'Annual')
 * @returns The frequency settings for the requested frequency, or one-time settings as fallback
 */
export const getFrequencySettings = (
	options: IDonationOptions,
	frequency: 'OneTime' | 'Monthly' | 'Annual'
): IDonationFrequencySettings => {
	const migratedOptions = migrateLegacyDonationOptions(options, true);

	switch (frequency) {
		case 'Monthly':
			return migratedOptions.monthlyDonationSettings || migratedOptions.oneTimeDonationSettings!;
		case 'Annual':
			return migratedOptions.annualDonationSettings || migratedOptions.oneTimeDonationSettings!;
		case 'OneTime':
		default:
			return migratedOptions.oneTimeDonationSettings!;
	}
};
